import { useMemo, useRef } from "react";
import type { VisitedLocation } from "../lib/types";
import { removeVisit, useFishStore } from "../lib/storage";
import { lifelistEntryFor } from "../lib/storage";
import { useDistanceUnit, fromKm, unitLabel } from "../lib/units";
import { PinIcon } from "../ui";
import { confirmAction } from "./ConfirmModal";

// Recent spots list — Option C (compact rows) + H add-ons:
//   • Regional grouping by proximity. Reefs within ~200 km of any spot in
//     an already-open group merge into it (union-find style), so nearby
//     coordinates cluster no matter what the place names look like.
//     Group label is picked from the members' own names: the shortest
//     "parent-ish" name wins (e.g. "Hawaiʻi" over "Kahaluʻu, Kahaluu-
//     Keauhou"), else the most common comma-tail, else "Around <first>".
//   • Long-press to remove. 550 ms hold triggers a native confirm; on OK
//     the spot leaves Recents but stays in your lifelist / trip / badge
//     history — this is a shortcut removal, not a data delete.

export interface RecentSpotsProps {
  spots: VisitedLocation[];
  onOpen: (v: VisitedLocation) => void;
}

// Comma-tail of a name — used as one of the candidate group labels
// (e.g. "Kahaluʻu, Kahaluu-Keauhou" → "Kahaluu-Keauhou").
function tailOf(name: string): string | null {
  const parts = name
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;
  return parts[parts.length - 1];
}

// Haversine great-circle distance in kilometres. Cheap enough to call
// pairwise for the 6-recent list without any indexing.
function distanceKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371;
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Two spots are "near" if they're within this many kilometres of each
// other. 200 km is roughly one Hawaiian island's worth of coast, or the
// stretch a single reef trip typically covers. Trip planning across a
// broader region still ends up in separate groups — that's the point.
const CLUSTER_KM = 200;

// Pick a label for a cluster: prefer the shortest / most parent-ish
// member name (dropping any comma tail, so "Hawaiʻi" beats "Kahaluʻu"
// which beats "Kahaluʻu, Kahaluu-Keauhou"). Fall back to the most-common
// tail, and finally to "Around <first>" so we always have SOMETHING.
function labelForCluster(spots: VisitedLocation[]): string {
  if (spots.length === 0) return "";
  // Shortest short-name — shortest usually reads as the most generic
  // "region-y" name (a country / state / island vs. a bay).
  const shortest = [...spots]
    .map((s) => shortName(s.name))
    .filter(Boolean)
    .sort((a, b) => a.length - b.length)[0];
  if (shortest) return shortest;

  // Tail vote — if a majority (or plurality) share "Hawaiʻi County",
  // use that.
  const tallies = new Map<string, number>();
  for (const s of spots) {
    const t = tailOf(s.name);
    if (!t) continue;
    tallies.set(t, (tallies.get(t) ?? 0) + 1);
  }
  const winner = [...tallies.entries()].sort((a, b) => b[1] - a[1])[0];
  if (winner) return winner[0];

  return `Around ${shortName(spots[0].name)}`;
}

// "Kahaluʻu, Kahaluu-Keauhou" → "Kahaluʻu" (drop the region tail so the
// row label isn't redundant with the region header above it).
function shortName(name: string): string {
  const parts = name
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return name;
  return parts.slice(0, -1).join(", ");
}

function humanAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 2 * day) return "yesterday";
  const days = Math.floor(diffMs / day);
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

// Bounded-time long-press detector. Fires `onLongPress` after HOLD_MS on
// a stationary press; cancels if the pointer moves more than MOVE_TOL
// pixels or lifts / leaves before the deadline. Ignores the fired press
// so the row's own onClick doesn't also trigger.
const HOLD_MS = 550;
const MOVE_TOL = 8;

function useLongPress(onLongPress: () => void) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  };

  return {
    onPointerDown: (e: React.PointerEvent) => {
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        fired.current = true;
        onLongPress();
      }, HOLD_MS);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!start.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      if (dx * dx + dy * dy > MOVE_TOL * MOVE_TOL) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    // Return whether the last press fired a long-press. The caller uses
    // this from onClick to swallow the click that follows the release.
    firedRef: fired,
  };
}

function Row({
  spot,
  loggedCount,
  totalCount,
  ago,
  radiusLabel,
  onOpen,
  onRemove,
}: {
  spot: VisitedLocation;
  loggedCount: number;
  totalCount: number;
  ago: string;
  radiusLabel: string;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const label = shortName(spot.name);
  const triggerRemove = async () => {
    const ok = await confirmAction({
      title: `Remove ${label} from Recents?`,
      body: "Your lifelist keeps everything you logged here.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) onRemove();
  };
  const longPress = useLongPress(() => {
    void triggerRemove();
  });
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          // Swallow the trailing click after a long-press.
          if (longPress.firedRef.current) {
            longPress.firedRef.current = false;
            return;
          }
          onOpen();
        }}
        onPointerDown={longPress.onPointerDown}
        onPointerMove={longPress.onPointerMove}
        onPointerUp={longPress.onPointerUp}
        onPointerCancel={longPress.onPointerCancel}
        onPointerLeave={longPress.onPointerLeave}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
        className="flex w-full items-center gap-3 rounded-xl bg-paper px-3 py-2.5 pr-14 text-left shadow-[inset_0_0_0_1px_rgba(10,39,51,0.07)] transition hover:bg-aqua/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep"
        style={{ touchAction: "manipulation" }}
        aria-label={`${label}. ${loggedCount} of ${totalCount} species logged.`}
      >
        <PinIcon className="h-4 w-4 flex-none text-aqua-deep" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[14.5px] font-semibold leading-tight text-ink">
            {label}
          </span>
          <span className="block truncate text-[11px] leading-tight text-meta">
            {radiusLabel}
            {ago ? ` · ${ago}` : ""}
          </span>
        </span>
        {totalCount > 0 && (
          <span className="flex-none rounded-full bg-aqua-deep/10 px-2 py-1 text-[10.5px] font-bold tracking-[0.06em] text-aqua-deep">
            {loggedCount} / {totalCount}
          </span>
        )}
      </button>
      {/* Visible remove control so touch + keyboard users don't need
          long-press to find the exit. Positioned absolutely to avoid
          nesting a button inside a button. */}
      <button
        type="button"
        aria-label={`Remove ${label} from Recents`}
        onClick={() => void triggerRemove()}
        className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-lg text-ink/60 hover:bg-coral/10 hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
      >
        ×
      </button>
    </div>
  );
}

export function RecentSpots({ spots, onOpen }: RecentSpotsProps) {
  // Subscribe to the lifelist so logged-count refreshes when the user
  // adds a sighting elsewhere on the page.
  useFishStore((s) => s.lifelist);
  const unit = useDistanceUnit();

  const groups = useMemo(() => {
    // Proximity cluster. Walk the spots in their existing order (most
    // recent first). For each spot, drop it into the first cluster that
    // has any member within CLUSTER_KM; otherwise open a new cluster.
    // Simple + n² but n is the length of Recents (6-ish) so it's fine.
    const clusters: VisitedLocation[][] = [];
    for (const s of spots) {
      const home = clusters.find((cluster) =>
        cluster.some((m) => distanceKm(m, s) <= CLUSTER_KM),
      );
      if (home) home.push(s);
      else clusters.push([s]);
    }
    // Clusters with a single member fold into an "Anywhere" bucket at
    // the top so we don't end up with a header for every single row.
    const ungrouped: VisitedLocation[] = [];
    const grouped: { region: string | null; spots: VisitedLocation[] }[] = [];
    for (const cluster of clusters) {
      if (cluster.length < 2) ungrouped.push(...cluster);
      else grouped.push({ region: labelForCluster(cluster), spots: cluster });
    }
    return ungrouped.length
      ? [{ region: null, spots: ungrouped }, ...grouped]
      : grouped;
  }, [spots]);

  return (
    <div className="px-1">
      <p className="mb-2 px-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-meta">
        Recent spots · {spots.length}
      </p>
      <div className="flex flex-col gap-4">
        {groups.map((g) => (
          <div key={g.region ?? "__any__"} className="flex flex-col gap-1.5">
            {g.region && (
              <p className="px-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-meta/80">
                {g.region}
              </p>
            )}
            {g.spots.map((s) => {
              const total = s.cached_species.length;
              const logged = s.cached_species.reduce(
                (n, sp) => (lifelistEntryFor(sp) ? n + 1 : n),
                0,
              );
              return (
                <Row
                  key={s.key}
                  spot={s}
                  loggedCount={logged}
                  totalCount={total}
                  ago={humanAgo(s.last_visited)}
                  radiusLabel={`${fromKm(s.radius_km, unit)} ${unitLabel(unit)}`}
                  onOpen={() => onOpen(s)}
                  onRemove={() => removeVisit(s.key)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
